import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { CookiesProvider } from 'react-cookie';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';

import { configuration } from './configuration';
import { AppRouter } from './router';
import { store } from './store';

import { asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import 'react-material-symbols/outlined';
import { PlatformNotification } from './components/platform-notification/platform-notification';
import { ModalProvider } from './hooks/modal-provider';
import './i18n';
import './main.css';
import { setLaunchDarklyClient } from './services/launchDarklyClient';

// Only init RUM when all credentials are present (avoids console errors in local dev; service required for source map matching)
if (
  configuration.ddApplicationId &&
  configuration.ddClientToken &&
  configuration.ddSite &&
  configuration.ddService
) {
  datadogRum.init({
    applicationId: configuration.ddApplicationId,
    clientToken: configuration.ddClientToken,
    site: configuration.ddSite,
    service: configuration.ddService,
    version: configuration.ddRelease || undefined,
    env: configuration.medeloopEnvironment,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    defaultPrivacyLevel: 'mask-user-input',
  });
  datadogRum.startSessionReplayRecording();
}

const queryClient = new QueryClient();

const App = () => {
  const ldClient = useLDClient();
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Initialize store after LaunchDarkly is ready
  useEffect(() => {
    if (ldClient && !isInitialized) {
      setLaunchDarklyClient(ldClient);
      store.dispatch({ type: 'INITIALIZE_STORE' });
      setIsInitialized(true);
    }
  }, [ldClient, isInitialized]);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <CookiesProvider
          defaultSetOptions={{
            path: '/',
            sameSite: 'lax',
            domain: configuration.cookiesDomain,
            secure: configuration.safeCookies,
            maxAge: 60 * 60 * 24 * 30,
          }}
        >
          <div id="modal-root" />
          <ModalProvider>
            <PlatformNotification />
            <AppRouter />
          </ModalProvider>
        </CookiesProvider>
      </QueryClientProvider>
    </Provider>
  );
};

const renderApp = async () => {
  try {
    const LDProvider = await asyncWithLDProvider({
      clientSideID: configuration.launchDarklyClientId,
      user: {
        key: 'user',
        anonymous: true,
      },
    });

    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <LDProvider>
          <App />
        </LDProvider>
      </React.StrictMode>,
    );
  } catch (error) {
    console.error('Failed to initialize LaunchDarkly:', error);

    // Fallback rendering without LaunchDarkly
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    );
  }
};

renderApp();
